/**
 * MessageErrorBoundary — isolate render failures to a single chat bubble.
 *
 * Without this, one malformed `content_json` row throws inside React render
 * and the top-level Sentry boundary in main.tsx unmounts the whole chat
 * surface. Wrapping each bubble means the worst case is a single inline
 * placeholder where the bad message would have been.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { BUBBLE_WIDTH } from "./MessageBubble";

interface Props {
  messageId: string;
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class MessageErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    Sentry.captureException(error, {
      tags: { boundary: "message-bubble" },
      extra: { messageId: this.props.messageId, componentStack: info.componentStack },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex justify-start animate-[fadeInUp_200ms_ease-out]">
          <div className={`${BUBBLE_WIDTH} rounded-2xl px-4 py-3 text-xs italic
                          bg-space-10 text-space-60 border border-space-20`}>
            Couldn't render this message.
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
